import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  KeyIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
  CodeBracketIcon,
  ArrowPathIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import toast from 'react-hot-toast';

export default function Developer() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [showRefreshToken, setShowRefreshToken] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [tokens, setTokens] = useState({
    accessToken: '',
    refreshToken: ''
  });

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    // Get tokens from localStorage and cookies
    const accessToken = localStorage.getItem('token') || Cookies.get('accessToken') || '';
    const refreshToken = Cookies.get('refreshToken') || '';
    setTokens({ accessToken, refreshToken });
  }, []);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(null), 2000);
  };

  const refreshAccessToken = async () => {
    try {
      if (!tokens.refreshToken) {
        toast.error('No refresh token available');
        return;
      }

      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: tokens.refreshToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error messages
        if (response.status === 401) {
          toast.error('Refresh token expired. Please login again.');
          // Redirect to login after a delay
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          toast.error(data.message || 'Failed to refresh token');
        }
        return;
      }
      
      // Update tokens
      const newAccessToken = data.tokens.accessToken;
      const newRefreshToken = data.tokens.refreshToken;
      
      // Store in localStorage and cookies
      localStorage.setItem('token', newAccessToken);
      Cookies.set('accessToken', newAccessToken, { expires: 1 });
      Cookies.set('refreshToken', newRefreshToken, { expires: 7 });
      
      setTokens({
        accessToken: newAccessToken,
        refreshToken: newRefreshToken
      });
      
      toast.success('Access token refreshed successfully');
    } catch (error) {
      // Network or parsing errors
      toast.error('Network error. Please try again.');
      console.error('Refresh error:', error);
    }
  };

  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const curlExample = `curl -H "Authorization: Bearer ${showAccessToken ? tokens.accessToken : '<YOUR_ACCESS_TOKEN>'}" \\
  http://localhost:3000/api/v1/projects`;

  // Decode JWT to show expiration
  const decodeToken = (token: string) => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch {
      return null;
    }
  };

  const getTokenInfo = (token: string) => {
    const decoded = decodeToken(token);
    if (!decoded) return { valid: false, message: 'Invalid token format' };
    
    const exp = decoded.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const isExpired = exp < now;
    const expiresIn = exp - now;
    
    if (isExpired) {
      return { valid: false, message: 'Token expired', expired: true };
    }
    
    const hours = Math.floor(expiresIn / (1000 * 60 * 60));
    const minutes = Math.floor((expiresIn % (1000 * 60 * 60)) / (1000 * 60));
    
    return { 
      valid: true, 
      message: `Expires in ${hours}h ${minutes}m`,
      expiresAt: new Date(exp).toLocaleString()
    };
  };

  const accessTokenInfo = tokens.accessToken ? getTokenInfo(tokens.accessToken) : null;
  const refreshTokenInfo = tokens.refreshToken ? getTokenInfo(tokens.refreshToken) : null;

  return (
      <div className="max-w-4xl mx-auto px-4 sm:px-0">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Developer Tools</h1>
          <p className="mt-2 text-sm text-gray-600">
            Access your API tokens and view documentation for integrating with Air Raid
          </p>
        </div>

        {/* User Info */}
        <div className="card mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Account Information</h2>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">User ID</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">{user?.id}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900">{user?.email}</dd>
            </div>
          </dl>
        </div>

        {/* Access Token */}
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <KeyIcon className="h-5 w-5 mr-2" />
              Access Token
            </h2>
            <button
              onClick={refreshAccessToken}
              className="btn-secondary text-sm flex items-center"
              disabled={!tokens.refreshToken}
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Refresh
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Use this token to authenticate API requests. It expires after 1 hour.
          </p>
          
          {accessTokenInfo && (
            <div className={`mb-4 text-sm ${accessTokenInfo.valid ? 'text-green-600' : 'text-red-600'}`}>
              {accessTokenInfo.message}
              {accessTokenInfo.valid && accessTokenInfo.expiresAt && (
                <span className="text-gray-500"> ({accessTokenInfo.expiresAt})</span>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Token</label>
              <div className="mt-1 flex">
                <input
                  type={showAccessToken ? 'text' : 'password'}
                  value={tokens.accessToken}
                  readOnly
                  className="form-input flex-1 font-mono text-xs"
                />
                <button
                  onClick={() => setShowAccessToken(!showAccessToken)}
                  className="ml-2 p-2 text-gray-400 hover:text-gray-600"
                >
                  {showAccessToken ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => copyToClipboard(tokens.accessToken, 'Access token')}
                  className="ml-2 p-2 text-gray-400 hover:text-gray-600"
                >
                  {copied === 'Access token' ? (
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Refresh Token */}
        <div className="card mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <KeyIcon className="h-5 w-5 mr-2" />
            Refresh Token
          </h2>
          
          <p className="text-sm text-gray-600 mb-4">
            Use this token to get new access tokens. It expires after 7 days. Keep it secure!
          </p>
          
          {refreshTokenInfo && !decodeToken(tokens.refreshToken) && (
            <div className="mb-4 text-sm text-gray-500">
              Note: Refresh tokens are opaque and cannot be decoded
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Token</label>
              <div className="mt-1 flex">
                <input
                  type={showRefreshToken ? 'text' : 'password'}
                  value={tokens.refreshToken}
                  readOnly
                  className="form-input flex-1 font-mono text-xs"
                />
                <button
                  onClick={() => setShowRefreshToken(!showRefreshToken)}
                  className="ml-2 p-2 text-gray-400 hover:text-gray-600"
                >
                  {showRefreshToken ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => copyToClipboard(tokens.refreshToken, 'Refresh token')}
                  className="ml-2 p-2 text-gray-400 hover:text-gray-600"
                >
                  {copied === 'Refresh token' ? (
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Example Usage */}
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CodeBracketIcon className="h-5 w-5 mr-2" />
            Example Usage
          </h2>
          
          <p className="text-sm text-gray-600 mb-4">
            Here&apos;s how to use your access token with curl:
          </p>

          <div className="bg-gray-900 rounded-lg p-4">
            <pre className="text-sm text-gray-300 overflow-x-auto">
              <code>{curlExample}</code>
            </pre>
            <button
              onClick={() => copyToClipboard(curlExample, 'Example')}
              className="mt-2 text-xs text-gray-400 hover:text-gray-200 flex items-center"
            >
              {copied === 'Example' ? (
                <>
                  <CheckIcon className="h-3 w-3 mr-1 text-green-400" />
                  Copied!
                </>
              ) : (
                <>
                  <DocumentDuplicateIcon className="h-3 w-3 mr-1" />
                  Copy example
                </>
              )}
            </button>
          </div>

          <div className="mt-6 space-y-2">
            <h3 className="text-sm font-medium text-gray-900">API Endpoints:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <span className="font-mono text-xs">GET /api/v1/projects</span> - List all projects</li>
              <li>• <span className="font-mono text-xs">POST /api/v1/projects</span> - Create a new project</li>
              <li>• <span className="font-mono text-xs">GET /api/v1/projects/:id</span> - Get project details</li>
              <li>• <span className="font-mono text-xs">GET /api/v1/schema/projects/:id/tables</span> - List project tables</li>
              <li>• <span className="font-mono text-xs">POST /api/v1/migrations/projects/:id</span> - Create migration</li>
            </ul>
          </div>
        </div>
      </div>
  );
}